import ServerErrorHandler from 'app/ErrorHandler';
import request from 'app/api/request';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { queryKeys as documentQueryKeys } from './documents/document-queries';
import { i18n } from 'app/utils/i18n';
import { EventType, dispatchMessage } from 'app/useToasts';
export const queryKeys = {
  style: 'STYLE',
  styles: 'STYLES',
  css: 'CSS',
};

export function useStyle({ organization, documentRevisionId, styleId, parsed = true }) {
  return useQuery(
    [queryKeys.style, organization, documentRevisionId, styleId],
    async () => {
      const params = new URLSearchParams();
      params.append('parsed', parsed);
      params.append('documentRevisionId', documentRevisionId);
      const res = await request.get(`/gaby/styles/${styleId}?${params.toString()}`);
      Object.entries(res.data).forEach(([key, value]) => {
        if (typeof value === 'string') {
          res.data[key] = value.replace(/\s*!default\s*$/, '');
        }
      });
      let dataContent = res.data?.content;
      if (dataContent && typeof dataContent === 'object') {
        dataContent = Object.fromEntries(
          Object.entries(dataContent).map(([key, value]) => [key, typeof value === 'string' ? value.replace(/\s*!default\s*$/, '') : value])
        );
      }
      return { ...res.data, content: dataContent };
    },
    {
      enabled: !!styleId && !!documentRevisionId,
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}

export function useStyles({ organization, documentRevisionId, styleIds, parsed = true }) {
  function makeRequests() {
    return styleIds.filter(Boolean).map(async (styleId) => {
      const params = new URLSearchParams();
      params.append('parsed', parsed);
      params.append('documentRevisionId', documentRevisionId);
      const res = await request.get(`/gaby/styles/${styleId}?${params.toString()}`);
      return res.data;
    });
  }
  return useQuery(
    [queryKeys.styles, organization, documentRevisionId, ...styleIds],
    async () => {
      return await Promise.all(makeRequests());
    },
    {
      enabled: (Array.isArray(styleIds) ? styleIds.filter(Boolean).length > 0 : false) && !!documentRevisionId,
      onError: ServerErrorHandler,
    }
  );
}

export function useCss({
  documentSuperId,
  documentRevisionId,
  useDraft,
  withStyle,
  withTheme = undefined,
  embedd,
  baseline = undefined,
  keepPreviousData = false,
}) {
  return useQuery(
    [queryKeys.css, documentSuperId, documentRevisionId, { useDraft, withStyle, withTheme, embedd, baseline }],
    async () => {
      const queryParams = [];
      if (useDraft) {
        queryParams.push('useDraft=true');
      }
      if (withStyle) {
        queryParams.push('withStyle=' + withStyle);
      }
      if (withTheme) {
        queryParams.push('withTheme=' + withTheme);
      }
      if (embedd) {
        queryParams.push('embedd=true');
      }
      if (baseline) {
        queryParams.push('baseline=' + baseline);
      }
      if (documentRevisionId) {
        queryParams.push('revisionId=' + documentRevisionId);
      }
      const query = '?' + queryParams.join('&');
      const response = await request.get(`/gaby/documents/${documentSuperId}/css${query}`);
      return response.data;
    },
    { enabled: !!documentSuperId, keepPreviousData, onError: ServerErrorHandler }
  );
}

export function useSaveStyle({ organization, documentSuperId, templateSuperId, pageId, documentRevisionId, styleIds }) {
  const queryClient = useQueryClient();
  const stylesQueryKey = [queryKeys.styles, organization, documentRevisionId, ...(styleIds || [])];

  return useMutation(
    async (content) => {
      const styleId = uuidv4();

      const action = {
        action: 'update_page_style',
        data: {
          styleId,
          organization,
          documentSuperId,
          pageId,
          templateSuperId,
          content,
        },
      };
      return await request.post(`/gaby/documents/${documentSuperId}/actions`, action);
    },
    {
      onMutate: async (newContent) => {
        // Cancel outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries(stylesQueryKey);

        // Snapshot previous value for rollback
        const previousStyles = queryClient.getQueryData(stylesQueryKey);

        // Optimistically update the cache
        if (previousStyles && Array.isArray(previousStyles)) {
          const updatedStyles = previousStyles.map((style, index) => {
            // Update the page style (typically index 1 when [graphicalStyle, pageStyle])
            // Match by checking if this style contains page-specific content
            if (style?.content && index === previousStyles.length - 1) {
              return { ...style, content: newContent };
            }
            return style;
          });
          queryClient.setQueryData(stylesQueryKey, updatedStyles);
        }

        return { previousStyles };
      },
      onError: (err, newContent, context) => {
        // Rollback to previous value on error
        if (context?.previousStyles) {
          queryClient.setQueryData(stylesQueryKey, context.previousStyles);
        }
        ServerErrorHandler(err);
      },
      onSettled: () => {
        // Always refetch after mutation to ensure consistency
        queryClient.invalidateQueries(queryKeys.css);
        queryClient.invalidateQueries(queryKeys.styles);
      },
    }
  );
}

export function useSaveFontStyle({ documentSuperId, styleId }) {
  const queryClient = useQueryClient();
  return useMutation(
    async (content) => {
      const action = {
        action: 'update_font_style',
        styleId,
        data: {
          content,
        },
      };
      return await request.post(`/gaby/documents/${documentSuperId}/actions`, action);
    },
    {
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n(`project-details.font-updated.success`),
          removeTime: 4000,
        });
        ServerErrorHandler(error);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.css);
        queryClient.invalidateQueries(documentQueryKeys.document, documentSuperId);
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n(`project-details.font-updated.success`),
          removeTime: 4000,
        });
      },
    }
  );
}

export function useDeleteFontStyle({ documentSuperId, styleId }) {
  const queryClient = useQueryClient();
  return useMutation(
    async () => {
      const action = {
        action: 'delete_font_style',
        styleId,
      };
      return await request.post(`/gaby/documents/${documentSuperId}/actions`, action);
    },
    {
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n(`project-details.font-deleted-error`),
          removeTime: 4000,
        });
        ServerErrorHandler(error);
      },

      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.css);
        queryClient.invalidateQueries(documentQueryKeys.document, documentSuperId);
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n(`project-details.font-deleted-success`),
          removeTime: 4000,
        });
      },
    }
  );
}
